import { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { getImageUrl } from '@/lib/api';

const ThemeContext = createContext({});

export function useTheme() {
  return useContext(ThemeContext);
}

const DEFAULT_COLORS = {
  primary: '#C9336B',
  secondary: '#8B1A4A',
  accent: '#E8A0BF',
  background: '#1A0A14',
  surface: '#2D1520',
  text: '#F5E6EC',
};

const GRADIENT_STOPS = [
  { offset: 0, color1: '#1A0A14', color2: '#2D1520' },
  { offset: 0.25, color1: '#1E0E1E', color2: '#3A1B2E' },
  { offset: 0.5, color1: '#2A1428', color2: '#4A2038' },
  { offset: 0.75, color1: '#1E0E1E', color2: '#3A1B2E' },
  { offset: 1, color1: '#1A0A14', color2: '#2D1520' },
];

const parseHex = (hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
const toHex = (r, g, b) => '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
const lerp = (a, b, t) => a + (b - a) * t;

export default function ThemeProvider({ children, settings }) {
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const themeType = settings?.themeType || 'gradient';
  const colors = settings?.themeColors || DEFAULT_COLORS;
  const bgImage = settings?.backgroundImage;

  // Detect whether the active background colour is light.
  // Gradient and photo themes always read as dark (overlays keep them dim).
  useEffect(() => {
    const body = document.body;
    let isLight = false;
    if (themeType === 'mono') {
      try {
        const [r, g, b] = parseHex(colors.background || '#1A0A14');
        // ITU-R BT.601 luminance; threshold tuned for comfortable contrast
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        isLight = lum > 170;
      } catch { isLight = false; }
    }
    body.classList.toggle('site-theme-light', isLight);
    return () => { body.classList.remove('site-theme-light'); };
  }, [themeType, colors.background]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-bg', colors.background);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-text', colors.text);
  }, [colors]);

  const updateGradient = useCallback(() => {
    const el = containerRef.current;
    if (!el || themeType !== 'gradient') return;
    const maxScroll = Math.max(document.body.scrollHeight - window.innerHeight, 1);
    const progress = Math.min(window.scrollY / maxScroll, 1);
    const idx = Math.floor(progress * (GRADIENT_STOPS.length - 1));
    const next = Math.min(idx + 1, GRADIENT_STOPS.length - 1);
    const t = (progress * (GRADIENT_STOPS.length - 1)) - idx;
    const c1a = parseHex(GRADIENT_STOPS[idx].color1);
    const c1b = parseHex(GRADIENT_STOPS[next].color1);
    const c2a = parseHex(GRADIENT_STOPS[idx].color2);
    const c2b = parseHex(GRADIENT_STOPS[next].color2);
    const col1 = toHex(lerp(c1a[0], c1b[0], t), lerp(c1a[1], c1b[1], t), lerp(c1a[2], c1b[2], t));
    const col2 = toHex(lerp(c2a[0], c2b[0], t), lerp(c2a[1], c2b[1], t), lerp(c2a[2], c2b[2], t));
    el.style.background = `linear-gradient(135deg, ${col1} 0%, ${col2} 50%, ${col1} 100%)`;
  }, [themeType]);

  useEffect(() => {
    if (themeType !== 'gradient') return;
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        updateGradient();
        rafRef.current = null;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    updateGradient();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [themeType, updateGradient]);

  const getInitialStyle = () => {
    if (themeType === 'photo' && bgImage) {
      return {
        backgroundImage: `linear-gradient(to bottom, ${colors.background}dd 0%, ${colors.background}99 50%, ${colors.background}dd 100%), url(${getImageUrl(bgImage)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      };
    }
    if (themeType === 'gradient') {
      return { background: `linear-gradient(135deg, ${GRADIENT_STOPS[0].color1} 0%, ${GRADIENT_STOPS[0].color2} 50%, ${GRADIENT_STOPS[0].color1} 100%)` };
    }
    return { backgroundColor: colors.background };
  };

  return (
    <ThemeContext.Provider value={{ themeType, colors, bgImage }}>
      <div ref={containerRef} style={{ minHeight: '100vh', color: colors.text, ...getInitialStyle() }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}
